import { CopyOutlined, TeamOutlined, UserAddOutlined, CheckOutlined } from '@ant-design/icons';
import LabelIcon from '../../components/LabelIcon/LabelIcon';
import Svg from '../../components/SVG/SVG';
// Design
import { message } from 'antd';
import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import Button from '../../components/Button/Button';
import { AuthContext } from '../../Guard/AuthContext';
import { RequestContext } from '../../helpers/requestContext';
import { useContactStore } from '../../hooks/useContactStore';
import { apiInstance } from '../../utils/api';
import DashboardTable from './DashboardTable';

const REQUESTS_URL = new URL(
  `${process.env.REACT_APP_API_URL}/api/crm/get-data?id=4735643-asjhdg&type=requests`
);

const dashboardRequestList = [
  {
    route: '/create-request?d7982047-eef2-435d-bf58-758c818433e4',
    icon: <LabelIcon icon="service" bg="purple" />,
    text: 'Звернення до адміністрації',
    btnBg: 'purple',
    btnIcon: 'create',
    btnText: 'Створити',
  },
  {
    route: '/create-request?f8b0f38b-60fd-42d9-b87d-39e704ed6d27',
    icon: <LabelIcon icon="work" bg="pink" />,
    text: 'Звернення до експлуатації',
    btnBg: 'pink',
    btnIcon: 'create',
    btnText: 'Створити',
  },
  {
    route: '/contacts/create',
    icon: (
      <div className="label-icon turquoise-bg">
        <TeamOutlined style={{ fontSize: 22 }} />
      </div>
    ),
    text: 'Новий співробітник',
    btnBg: 'turquoise',
    btnIcon: <UserAddOutlined />,
    btnText: 'Створити',
    permissionKey: 'marketing', // Проверка прав доступа как в Sidebar
  },
];

const DashboardItem = ({ dashboardRequesItem, setCreateRequestFromDashboard }) => {
  const { icon, text, btnBg, route, btnIcon, btnText } = dashboardRequesItem;
  const { push } = useHistory();

  const createHandler = () => {
    setCreateRequestFromDashboard(true);
    push(route);
  };

  // Если btnIcon это React компонент, отображаем кнопку с иконкой отдельно
  if (React.isValidElement(btnIcon)) {
    return (
      <div className="dashboard-request-item">
        {icon}
        <div className="dashboard-request-item__text text-black-normal-f16-l28">{text}</div>
        <Button
          text={
            <span
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                width: '100%',
                fontSize: '14px',
                padding: '0',
              }}
            >
              <span style={{ flex: 1 }}>{btnText}</span>
              <span style={{ fontSize: '18px', display: 'flex', alignItems: 'center' }}>
                {btnIcon}
              </span>
            </span>
          }
          bg={btnBg}
          onClick={() => createHandler()}
          style={{
            minHeight: '36px',
            fontSize: '14px',
            padding: '8px 16px',
            width: '140px',
            minWidth: '140px',
          }}
        />
      </div>
    );
  }

  // Обычная кнопка с SVG иконкой
  return (
    <div className="dashboard-request-item">
      {icon}
      <div className="dashboard-request-item__text text-black-normal-f16-l28">{text}</div>
      <Button
        text={
          <span
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              width: '100%',
              fontSize: '14px',
            }}
          >
            {btnText}
            <span style={{ display: 'flex', alignItems: 'center' }}>
              <Svg name={btnIcon} />
            </span>
          </span>
        }
        bg={btnBg}
        onClick={() => createHandler()}
        style={{
          minHeight: '36px',
          fontSize: '14px',
          padding: '8px 16px',
          width: '140px',
          minWidth: '140px',
        }}
      />
    </div>
  );
};

const Dashboard = () => {
  const [requests, setRequests] = useState([]);
  const [copied, setCopied] = useState(false);
  const { currentContactStore } = useContactStore();
  const { setCreateRequestFromDashboard } = useContext(RequestContext);
  const { currentContactStore: authContactStore } = useContext(AuthContext);

  // Функция для проверки доступа (как в Sidebar)
  const isDenied = (value) => value === false || value === 'false' || value === 0 || value === '0';

  // Фильтруем элементы дашборда по правам доступа
  const filteredDashboardList = useMemo(() => {
    return dashboardRequestList.filter(({ permissionKey }) => {
      if (!permissionKey) {
        return true; // Если нет ключа проверки - показываем
      }

      const permissionValue = authContactStore?.[permissionKey];
      if (permissionValue === undefined) {
        return true; // Если значение не определено - показываем
      }

      return !isDenied(permissionValue); // Скрываем только если явно запрещено
    });
  }, [authContactStore]);

  useEffect(() => {
    if (currentContactStore.requests === undefined) {
      return; // Ждем пока появится значение requests
    }

    if (currentContactStore.requests === false) {
      setRequests([]);
      return;
    }

    apiInstance(REQUESTS_URL, 'GET')
      .then((response) => {
        const closeStatus = '6e5f4218-f46b-1410-fe9a-0050ba5d6c38';

        if (response?.rows && Array.isArray(response.rows)) {
          const filteredRequests = response.rows.filter(
            (request) => request.Status?.value !== closeStatus
          );
          setRequests(filteredRequests.slice(0, 6));
        } else {
          setRequests([]);
        }
      })
      .catch(() => {
        setRequests([]);
      });
  }, [currentContactStore]);

  const newStatus = '3859c6e7-cbcb-486b-ba53-77808fe6e593';
  const calcNewRequests = requests.filter(({ Status }) => Status.value === newStatus).length;

  // Функция для копирования номера в буфер обмена
  const handleCopyPhoneNumber = async () => {
    const phoneNumber = '+380503215243'; // Форматируем номер без пробелов
    try {
      await navigator.clipboard.writeText(phoneNumber);
      message.success('Номер скопійовано!'); // Уведомление на украинском
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      message.error('Не вдалося скопіювати номер'); // Ошибка на украинском
    }
  };

  const dashboardRequestCreateBlock = useMemo(() => {
    if (currentContactStore?.requests === true) {
      return (
        <div className="box dashboard-request">
          <div className="dashboard-request__title text-black-medium-f22-l30">Що зробити?</div>
          <div className="dashboard-request__list">
            {filteredDashboardList.map((dashboardRequesItem) => (
              <DashboardItem
                key={dashboardRequesItem.route}
                dashboardRequesItem={dashboardRequesItem}
                setCreateRequestFromDashboard={setCreateRequestFromDashboard}
              />
            ))}
          </div>
          <div className="dashboard-request__description text-grey-dark-normal-f16-l28">
            <span className="text-grey-dark-bold-f16-l28">Увага!</span>
            <br />
            Адміністрація розглядає лише звернення щодо:
            <br />
            - ввезення / вивезення товару
            <br />
            - нічних робіт
            <br />
            Звернення щодо інцидентів і скарг — до{' '}
            <span className="text-grey-dark-bold-f16-l28">служби експлуатації.</span>
            <br />
            Інші питання — до{' '}
            <span className="text-grey-dark-bold-f16-l28">комерційної служби</span>
            <br /> або <span className="text-grey-dark-bold-f16-l28">відділу маркетингу.</span>
          </div>
        </div>
      );
    } else {
      return null;
    }
  }, [currentContactStore, setCreateRequestFromDashboard, filteredDashboardList]);

  const dashboardRequestListBlock = useMemo(() => {
    return (
      <div className="boxes__col right">
        {!requests.length ? (
          <div className="box dashboard-messages loading">
            <div className="dashboard-messages__text text-grey-dark-normal-f16-l28">
              Немає звернень
            </div>
          </div>
        ) : (
          <div className="box dashboard-messages">
            <div className="dashboard-messages__title text-black-medium-f22-l30">
              Сповіщення про звернення
            </div>
            <div className="dashboard-messages__text text-grey-dark-normal-f16-l28">
              {calcNewRequests} в очікуванні узгодження
            </div>
            <DashboardTable requests={requests} />
          </div>
        )}
      </div>
    );
  }, [requests, calcNewRequests]);

  const dashboardBlock = useCallback(() => {
    return (
      <div className="dashboard">
        <div className="boxes">
          <div className="boxes__col left">
            <div className="box dashboard-support">
              <div className="dashboard-support__title text-black-medium-f22-l30">
                Маєте запитання?
              </div>
              <div className="dashboard-support__text text-grey-dark-normal-f16-l28">
                Зв'яжіться з вашим персональним менеджером ТРЦ Respublika Park
              </div>
              <div className="dashboard-support__inner">
                <div className="dashboard-support__name text-purple-dark-medium-f16-l20">
                  Телефон по загальним питанням
                </div>
                 <div
                  className="dashboard-support__number text-pink-medium-f16-l20"
                  onClick={handleCopyPhoneNumber}
                  style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px' }}
                >
                  <span>+38 050 321 52 43</span>
                  {copied ? (
                    <CheckOutlined style={{ fontSize: '16px', color: '#1aa053' }} />
                  ) : (
                    <CopyOutlined style={{ fontSize: '16px' }} />
                  )}
                </div>
              </div>
            </div>
            {dashboardRequestCreateBlock}
          </div>
          {dashboardRequestListBlock}
        </div>
      </div>
    );
  }, [dashboardRequestCreateBlock, dashboardRequestListBlock]);

  return dashboardBlock();
};

export default Dashboard;
